type AttributeType = number | boolean | string | number[];

/** Define the attributes that we expect in a "Parameters/Scan0" file. */
type ScanAttributes = {
  /** The original scan id (GUIDv4) */
  ScanId?: string;
  /** Time when the scan was recorded (Unix timestamp in seconds as a double) */
  timestamp?: number;
  /** Scan group ID (GUIDv4) */
  ScanGroup?: string;
  /** Scan group name (string) */
  ScanGroupName?: string;
}

/**
 * Get the ScanId attribute from a attribute container file,
 * For a Focus scan, that file is named "Parameters/Scan0"
 * @param file File to parse
 * @returns Scan UUID or undefined
 */
export async function getScanAttributesFromFile(file: File): Promise<ScanAttributes> {
  const content = await file.text();
  const attributes = parseAttributeContainer(content);
  // This might be the cluster ID? attributes.GroupId
  return attributes as ScanAttributes;
}

/**
 * Parse an SCENE / Focus attribute container string into a dictionary
 *
 * Transformed from this original python code:
 * <https://dev.azure.com/faro-connect/Registration/_git/C2CWorker?path=/Common/registration_common/attribute_container.py>
 *
 * This is incomplete, maybe even wrong, because it does not handle nested
 * attribute containers (SubContainers).
 * The first attribute found will be used, and the rest will be ignored.
 *
 * Also not all types are supported, and will be returned as string.
 * e.g. DVector, UInt32Vector, ...
 *
 * But it should be good enough for our purposes.
 */
export function parseAttributeContainer(data: string):
  Record<string, AttributeType> {
  // regex to match the Attr "Type", "Name" and "Value". Example:
  // Attr<Type> {
  // Name "Key"
  // Value "The value"
  // }
  const attrRegex = /Attr<([^>]+)>\s*{\s*Name\s*"([^"]+)"\s*Value\s*([^\n\r\f\v]*)/g;

  // Create a dictionary to store results
  const result: Record<string, AttributeType> = {};

  // Find all matches using matchAll
  const matches = Array.from(data.matchAll(attrRegex));

  for (const match of matches) {
    const [, attrType, attrName, attrValue] = match;

    // skip if attrName is in result already (to not overwrite values)
    if (attrName in result) {
      continue;
    }

    switch (attrType) {
      case "int":
        result[attrName] = parseInt(attrValue, 10);
        break;
      case "double":
        result[attrName] = parseFloat(attrValue);
        break;
      case "bool":
        result[attrName] = attrValue.toLowerCase() === "true";
        break;
      case "Vec3dd":
        result[attrName] = attrValue.split(" ").map(Number);
        break;
      default:
        // Uuid, String, Unknown
        result[attrName] = attrValue.replace(/^"(.*)"$/, "$1");
    }
  }

  return result;
}